import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useRef, useEffect, useCallback } from 'react'
import { QrReader, type OnResultFunction } from 'react-qr-reader'
import * as toast from '@/components/ui/toast'

export const Route = createFileRoute('/_authenticated/_dashboard/scan')({
  component: RouteComponent,
})

const border = `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%236713DBFF' stroke-width='10' stroke-dasharray='50%25' stroke-dashoffset='25%25' stroke-linecap='square'/%3e%3c/svg%3e")`

function RouteComponent() {
  const isMobile = useIsMobile()
  const videoRef = useRef<HTMLVideoElement>(null)
  const navigate = useNavigate()
  useEffect(() => {
    if (!videoRef.current) {
      videoRef.current = document.getElementById('camera') as HTMLVideoElement
    }
    if (videoRef.current) {
      videoRef.current.muted = true
      videoRef.current.play()
    }

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        ;(videoRef.current.srcObject as MediaStream)
          .getVideoTracks()
          .forEach((track) => {
            track.stop()
          })
        videoRef.current.srcObject = null
      }
    }
  }, [])

  const done = useRef(false)
  const handleScan: OnResultFunction = useCallback(
    (result, error) => {
      if (done.current) return

      if (error) return
      if (!result) {
        return toast.error('No QR code found')
      }
      const text = new URL(result.getText())
      if (text.origin !== location.origin) {
        return toast.error('Not a valid LineWise QR code')
      }

      navigate({ to: text.pathname })
      done.current = true
    },
    [navigate],
  )

  return (
    <main
      className={cn('main-container relative', isMobile && 'mb-20 flex-col')}
    >
      {
        <QrReader
          className="absolute top-0 left-0 h-full w-full"
          constraints={{ facingMode: 'environment' }}
          videoId="camera"
          videoStyle={{ objectFit: 'cover' }}
          onResult={handleScan}
          videoContainerStyle={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      }
      <div className="top-0 left-0 flex h-full w-full flex-col items-center justify-center">
        <h1 className="bg-background/50 absolute top-1/16 left-1/2 -translate-x-1/2 rounded-full px-8 py-4 text-lg font-bold">
          Scan QR Code
        </h1>
        <div
          className="absolute top-1/2 left-1/2 aspect-square min-h-[30%] min-w-[30%] -translate-x-1/2 -translate-y-1/2"
          style={{ backgroundImage: border }}
        ></div>
      </div>
    </main>
  )
}
